import React from 'react';
import YouTube from 'react-youtube';

import type { DocumentFileType } from '@poseidon/types/types/document.type';
import { ButtonA } from '@poseidon/components/Input2';
import { Alerts, General } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';
import { detectEmbedPlatform } from '@poseidon/shared-utils/documentEmbeds';
import { AnimatePresence, motion } from 'framer-motion';

import { DocImage } from '~/components/CDNImage';
import { PDFViewer } from '~/components/PDFViewer';

type DocumentSlide = {
  id: string;
  url: string;
  webp: string | null;
  sizes: Array<{ w: number; h: number; url: string }> | null;
};
type DocumentPublic = {
  fileContentType: string;
  fileUrl: string;
  documentRepresentation: DocumentSlide[];
  primaryColor: string;
  ogImageUrl: string | null;
};

interface DocumentContentProps {
  data: { sharing: DocumentPublic; userTeam: { hubspot_owner_id: string | null } };
}

export const DocumentContent = ({ data: { sharing: doc, userTeam } }: DocumentContentProps) => {
  const { type, videoId } = getSupportedFileType(doc.fileContentType, doc.fileUrl);
  const [iframeLoaded, setIframeLoaded] = React.useState(false);

  const opts = {
    rel: '0',
    autoplay: '1',
    modestbranding: '1',
  };

  const images = doc.documentRepresentation;
  // The document URL must be encoded before being placed in the `url` query
  // parameter. PDF public URLs come from GCS `publicUrl()`, which percent-encodes
  // the object path (spaces -> %20, path separators -> %2F, etc.). Concatenating
  // that raw into `?url=...` leaves stray `%`/`&` in the query string, so Google
  // Docs Viewer receives a truncated/malformed URL and the PDF never loads.
  const pdfUrl = 'https://docs.google.com/viewer?url=' + encodeURIComponent(doc.fileUrl) + '&embedded=true';
  const backgroundColor = type == 'web' ? 'white' : doc.primaryColor;

  const needsLoadingOverlay = ['web', 'pdf', 'canva'].includes(type) && (images == null || images.length === 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ backgroundColor }}
      className="relative h-full min-w-0 shrink grow bg-black [&_iframe]:border-none [&_iframe]:outline-0">
      {/* Loading overlay */}
      <AnimatePresence>
        {needsLoadingOverlay && !iframeLoaded && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 z-10 flex items-center justify-center"
            style={{ backgroundColor }}>
            <WaveSpinner />
          </motion.div>
        )}
      </AnimatePresence>

      {type === 'pdf' && (images == null || images.length === 0) && (
        <iframe
          style={{ width: '100%', height: '100%' }}
          src={pdfUrl}
          frameBorder="0"
          onLoad={() => setIframeLoaded(true)}
        />
      )}
      {type === 'pdf' && images != null && images.length > 0 && <PDFViewer images={images} autoplay />}
      {type === 'image' && <DocImage doc={doc} className="object-contain" />}
      {type === 'youtube-clip' && videoId != null && (
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/clip/${videoId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
      {type === 'youtube' && (
        <YouTube className="size-full" iframeClassName="size-full" videoId={videoId} opts={opts} />
      )}
      {type === 'vimeo' && videoId != null && (
        <iframe
          width="100%"
          height="100%"
          frameBorder="0"
          src={`https://player.vimeo.com/video/${videoId}?transparent=0&title=0&byline=0&portrait=0`}
        />
      )}
      {type === 'muse' && videoId != null && (
        <iframe
          src={`https://muse.ai/embed/${videoId}?volume=75`}
          style={{ width: '100%', height: '100%', border: '0' }}
          allowFullScreen
        />
      )}
      {type === 'canva' && (
        <iframe
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            padding: 0,
            margin: 0,
          }}
          src={doc.fileUrl + '?embed'}
          onLoad={() => setIframeLoaded(true)}
        />
      )}
      {type === 'web' && (
        <iframe
          width="100%"
          height="100%"
          frameBorder="0"
          onLoad={() => setIframeLoaded(true)}
          src={
            userTeam.hubspot_owner_id
              ? (() => {
                  try {
                    const url = new URL(doc.fileUrl, window.location.origin);
                    url.searchParams.set('hubspot_owner_id', userTeam.hubspot_owner_id);
                    return url.toString();
                  } catch {
                    const sep = doc.fileUrl.includes('?') ? '&' : '?';
                    return doc.fileUrl + sep + 'hubspot_owner_id=' + encodeURIComponent(userTeam.hubspot_owner_id);
                  }
                })()
              : doc.fileUrl
          }
        />
      )}
      {type === 'custom-video' && (
        // `absolute inset-0` keeps the video out of flow so its large intrinsic
        // height (native resolution) can't inflate the flex column's min-content
        // height and overflow the viewport. `object-contain` then fits it to the
        // available space: full height, auto width, scaled down to fit horizontally.
        <video controls autoPlay className="absolute inset-0 size-full object-contain outline-0">
          <source src={doc.fileUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      {type === 'loom' && videoId != null && (
        <iframe
          src={`https://www.loom.com/embed/${videoId}`}
          style={{ width: '100%', height: '100%', border: '0' }}
          allowFullScreen
        />
      )}
      {type === 'descript' && videoId != null && (
        <iframe
          src={`https://share.descript.com/embed/${videoId}`}
          style={{ width: '100%', height: '100%', border: '0' }}
          allowFullScreen
        />
      )}
      {type === 'unknown' && (
        <div className="flex size-full items-center justify-center">
          <div className="flex max-w-sm flex-col items-center gap-4 rounded-xl bg-white/10 p-8 text-center text-white">
            <Alerts.AlertCircle className="size-12 text-white/60" />
            <div>
              <div className="text-lg font-semibold">Unsupported File</div>
              <p className="mt-1 text-sm text-white/60">This file type cannot be previewed in the browser.</p>
            </div>
            <ButtonA
              href={doc.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="blue"
              icon={<General.Download01 />}>
              Download File
            </ButtonA>
          </div>
        </div>
      )}
    </motion.div>
  );
};

type LinkDetailReturn = {
  type: DocumentFileType;
  videoId?: string;
  iframeUrl?: string;
};

const getSupportedFileType = (contentType: string, fileUrl: string): LinkDetailReturn => {
  if (contentType === 'application/pdf') {
    return { type: 'pdf' };
  } else if (['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(contentType)) {
    return { type: 'image' };
  } else if (contentType.startsWith('video')) {
    return { type: 'custom-video' };
  } else if (contentType.startsWith('custom-link')) {
    const { type, videoId, iframeUrl } = _getCustomLinkDetails(fileUrl);
    return { type: type, videoId, iframeUrl };
  } else {
    return { type: 'unknown' };
  }
};

const _getCustomLinkDetails = (url: string): LinkDetailReturn => {
  const embedResult = detectEmbedPlatform(url);
  if (embedResult) {
    return {
      type: embedResult.platform.type,
      videoId: embedResult.id,
      iframeUrl: embedResult.embedUrl,
    };
  }
  return { type: 'web' };
};
